import { Link } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { Check, X, ArrowRight } from 'lucide-react'
import { breadcrumbList, itemList, jsonLdScript, webPage } from '@/lib/schema'
import { StartFreeButton } from '@/components/marketing/free-plan-cta'
import { useFreePlan } from '@/hooks/use-free-plan'
import { EDGE_LOCATION_COUNT } from '@/lib/check-locations'

export function comparisonScripts(slug: string, competitor: string) {
  const url = `https://uptrack.app/compare/${slug}`
  return [
    jsonLdScript(webPage({
      name: `Uptrack vs ${competitor}`,
      description: `Uptrack vs ${competitor}: plans, check intervals, status pages and how each confirms downtime.`,
      url,
    })),
    jsonLdScript(breadcrumbList([
      { name: 'Home', url: 'https://uptrack.app' },
      { name: 'Compare', url: 'https://uptrack.app/compare' },
      { name: `${competitor} Alternative`, url },
    ])),
    jsonLdScript(itemList(
      `Uptrack vs ${competitor}`,
      [
        { name: 'Uptrack', url: 'https://uptrack.app', description: 'Uptime monitoring with consecutive-check alert confirmation and 30-second checks.' },
        { name: competitor, url, description: `${competitor} uptime monitoring — see how it compares to Uptrack.` },
      ],
      url,
    )),
  ]
}

export interface ComparisonFeature {
  feature: string
  uptrack: string
  competitor: string
  uptrackWins?: boolean
}

const normalize = (v: string) => v.toLowerCase().replace(/[^a-z0-9$%]+/g, ' ').trim()

/** Both products give the same answer for this row (e.g. "30 sec" vs "30 sec"). */
export function isTie(f: ComparisonFeature): boolean {
  return normalize(f.uptrack) === normalize(f.competitor)
}

export interface PainPoint {
  title: string
  description: string
}

export interface ComparisonData {
  competitor: string
  tagline: string
  heroDescription: string
  features: ComparisonFeature[]
  painPoints: PainPoint[]
  differentiators: string[]
  notForYou: string[]
}

function Section({ children }: { children: React.ReactNode }) {
  return (
    <section className="border-t border-border">
      <div className="container mx-auto px-4 py-12 md:py-16">{children}</div>
    </section>
  )
}

function SectionHeading({ title, children }: { title: string; children?: React.ReactNode }) {
  return (
    <div>
      <h2 className="text-2xl font-semibold tracking-tight">{title}</h2>
      {children && <p className="mt-2 max-w-xl text-sm text-muted-foreground">{children}</p>}
    </div>
  )
}

export function ComparisonPage({ data }: { data: ComparisonData }) {
  const free = useFreePlan()
  const wins = data.features.filter((f) => f.uptrackWins)
  // Rows where both sides say the same thing are ties: they belong in the table, not in either list.
  const losses = data.features.filter((f) => !f.uptrackWins && !isTie(f))

  return (
    <>

      <>
        {/* Hero */}
        <section>
          <div className="container mx-auto px-4 py-12 md:py-20">
            <h1 className="max-w-3xl text-4xl font-semibold tracking-tight md:text-5xl">
              Uptrack vs {data.competitor}
            </h1>
            <p className="mt-4 max-w-2xl text-lg text-muted-foreground">
              {data.tagline}
            </p>
            <p className="mt-6 max-w-2xl border-y border-border py-3 text-sm">
              <span className="font-medium">
                {free.isOpen
                  ? 'Every Uptrack plan checks at 30 seconds: free, or from $3/mo.'
                  : 'Every Uptrack plan checks at 30 seconds — from $3/mo.'}
              </span>{' '}
              <span className="text-muted-foreground">
                Alerts fire only after 2–5 consecutive failed checks.{' '}
                {free.isOpen ? '30-day money-back guarantee on paid plans.' : '30-day money-back guarantee.'}
              </span>
            </p>
          </div>
        </section>

        {/* Feature Comparison Table */}
        <section className="border-t border-border">
          <div className="container mx-auto px-4 py-12 md:py-16">
            <div className="max-w-4xl overflow-x-auto" tabIndex={0} role="region" aria-label="Scrollable table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="pb-3 pr-4 text-left text-xs font-medium text-muted-foreground">Feature</th>
                    <th className="px-4 pb-3 text-left text-xs font-medium text-primary">Uptrack</th>
                    <th className="pb-3 pl-4 text-left text-xs font-medium">{data.competitor}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.features.map((row) => (
                    <tr key={row.feature} className="border-b border-border last:border-0">
                      <td className="py-3 pr-4 font-medium">{row.feature}</td>
                      <td className="px-4 py-3">
                        <span className={row.uptrackWins ? 'font-medium text-primary' : ''}>
                          {row.uptrack}
                        </span>
                      </td>
                      <td className="py-3 pl-4 text-muted-foreground">
                        {row.competitor}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Win/Lose Summary */}
        <Section>
          <div className="grid max-w-4xl gap-x-10 gap-y-8 md:grid-cols-2">
            <div>
              <h2 className="flex items-center gap-2 font-medium text-status-up">
                <Check className="h-4 w-4" />
                Where Uptrack wins
              </h2>
              <ul className="mt-3 divide-y divide-border border-y border-border">
                {wins.map((f) => (
                  <li key={f.feature} className="flex items-start gap-2 py-2.5 text-sm">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-status-up" />
                    <span><span className="font-medium">{f.feature}</span> — {f.uptrack}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h2 className="flex items-center gap-2 font-medium text-muted-foreground">
                <X className="h-4 w-4" />
                Where {data.competitor} differs
              </h2>
              <ul className="mt-3 divide-y divide-border border-y border-border">
                {losses.map((f) => (
                  <li key={f.feature} className="flex items-start gap-2 py-2.5 text-sm text-muted-foreground">
                    <span aria-hidden="true" className="mt-0.5 shrink-0">—</span>
                    <span><span className="font-medium">{f.feature}</span> — {f.competitor}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Section>

        {/* Pain Points */}
        <Section>
          <div className="grid gap-8 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <SectionHeading title={`What to weigh: ${data.competitor} vs Uptrack`}>
                {data.heroDescription}
              </SectionHeading>
            </div>
            <ul className="divide-y divide-border border-y border-border lg:col-span-8">
              {data.painPoints.map((point) => (
                <li key={point.title} className="py-4">
                  <h3 className="font-medium">{point.title}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{point.description}</p>
                </li>
              ))}
            </ul>
          </div>
        </Section>

        {/* Differentiators */}
        <Section>
          <div className="grid gap-8 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <SectionHeading title="What Uptrack does differently" />
            </div>
            <ul className="divide-y divide-border border-y border-border lg:col-span-8">
              {data.differentiators.map((item) => (
                <li key={item} className="flex items-start gap-3 py-3">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  <span className="text-sm leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </Section>

        {/* Honesty Section */}
        <Section>
          <div className="grid gap-8 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <SectionHeading title="Uptrack might not be for you">
                We believe honesty builds more trust than claiming to be the best at everything.
              </SectionHeading>
            </div>
            <ul className="divide-y divide-border border-y border-border lg:col-span-8">
              {data.notForYou.map((item) => (
                <li key={item} className="flex items-start gap-3 py-3">
                  <X className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="text-sm leading-relaxed text-muted-foreground">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </Section>

        {/* Free tool callout */}
        <Section>
          <div className="grid items-center gap-6 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <p className="text-sm font-medium text-status-up">Free tool — no signup needed</p>
              <h2 className="mt-2 text-xl font-semibold tracking-tight">Check any website from {EDGE_LOCATION_COUNT} locations right now</h2>
              <p className="mt-2 max-w-xl text-sm text-muted-foreground">
                Our checker tests from {EDGE_LOCATION_COUNT} locations on six continents at once. See response times and status codes per location.
              </p>
            </div>
            <div className="lg:col-span-4 lg:text-right">
              <Button variant="outline" size="lg" asChild>
                <Link to="/tools/is-website-down">
                  Try "Is It Down?" Tool
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </Section>

        {/* CTA */}
        <Section>
          <div className="grid items-center gap-6 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <h2 className="text-2xl font-semibold tracking-tight">Ready to try Uptrack?</h2>
              <p className="mt-2 max-w-lg text-muted-foreground">
                {free.isOpen
                  ? 'Start free or choose a plan, and set up your first monitor in under a minute.'
                  : 'Choose a subscription and set up your first monitor in under a minute.'}
              </p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row lg:col-span-5 lg:justify-end">
              <StartFreeButton />
              <Button size="lg" variant={free.isOpen ? 'outline' : 'default'} asChild>
                <Link to="/pricing">
                  See plans
                  {!free.isOpen && <ArrowRight className="ml-2 h-4 w-4" />}
                </Link>
              </Button>
            </div>
          </div>
        </Section>
      </>

      </>
  )
}
